import { useState, useEffect } from 'react';
import { X, Check, AlertCircle, Package, TrendingDown, DollarSign, Bell } from 'lucide-react';

export default function NotificationPanel({ isOpen, onClose }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  // Carregar notificações salvas do localStorage
  const getSavedNotifications = () => {
    try {
      const saved = localStorage.getItem('notifications_state');
      return saved ? JSON.parse(saved) : { read: [], deleted: [] };
    } catch {
      return { read: [], deleted: [] };
    }
  };

  // Salvar estado das notificações
  const saveNotificationState = (state) => {
    try {
      localStorage.setItem('notifications_state', JSON.stringify(state));
    } catch (error) {
      console.error('Erro ao salvar notificações:', error);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      // Importar APIs dinamicamente para evitar erros de inicialização
      const { productsAPI, stockAPI, cashflowAPI } = await import('../api');
      
      // Buscar dados reais da API
      const [products, movements, transactions] = await Promise.all([
        productsAPI.getAll().catch(() => []),
        stockAPI.getAll().catch(() => []),
        cashflowAPI.getAll().catch(() => [])
      ]);

      const realNotifications = [];

      // 1. Produtos com estoque baixo
      if (products && Array.isArray(products)) {
        products.forEach(product => {
          if (product.stock <= product.minStock && product.stock > 0) {
            realNotifications.push({
              id: `low-stock-${product.id}`,
              type: 'warning',
              title: 'Estoque Baixo',
              message: `${product.name} está com apenas ${product.stock} unidades em estoque`,
              icon: Package,
              color: 'amber',
              time: calcularTempo(product.updatedAt || product.createdAt),
              read: false
            });
          }
        });

        // 2. Produtos sem estoque
        products.forEach(product => {
          if (product.stock === 0) {
            realNotifications.push({
              id: `no-stock-${product.id}`,
              type: 'error',
              title: 'Estoque Crítico',
              message: `${product.name} sem estoque disponível!`,
              icon: AlertCircle,
              color: 'red',
              time: calcularTempo(product.updatedAt || product.createdAt),
              read: false
            });
          }
        });
      }

      // 3. Movimentações recentes (últimas 24h)
      if (movements && Array.isArray(movements) && products && Array.isArray(products)) {
        const ontem = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const recentMovements = movements.filter(m => new Date(m.date) > ontem);
        
        recentMovements.slice(0, 3).forEach(movement => {
          const produto = products.find(p => p.id === movement.productId);
          if (produto) {
            realNotifications.push({
              id: `movement-${movement.id}`,
              type: 'info',
              title: movement.type === 'IN' ? 'Entrada de Estoque' : 'Saída de Estoque',
              message: `${movement.quantity} unidades de ${produto.name} (${movement.reason || 'sem motivo'})`,
              icon: movement.type === 'IN' ? TrendingDown : Package,
              color: 'blue',
              time: calcularTempo(movement.date),
              read: true
            });
          }
        });
      }

      // 4. Transações financeiras recentes
      if (transactions && Array.isArray(transactions)) {
        const ontem = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const recentTransactions = transactions.filter(t => new Date(t.date) > ontem);
        
        recentTransactions.slice(0, 2).forEach(transaction => {
          realNotifications.push({
            id: `transaction-${transaction.id}`,
            type: transaction.type === 'INCOME' ? 'success' : 'info',
            title: transaction.type === 'INCOME' ? 'Receita Registrada' : 'Despesa Registrada',
            message: `${transaction.type === 'INCOME' ? 'Receita' : 'Despesa'} de R$ ${transaction.amount.toFixed(2)} - ${transaction.category}`,
            icon: DollarSign,
            color: transaction.type === 'INCOME' ? 'green' : 'blue',
            time: calcularTempo(transaction.date),
            read: true
          });
        });
      }

      // Aplicar estado salvo (lidas/deletadas)
      const savedState = getSavedNotifications();
      
      // Filtrar notificações deletadas
      const filteredNotifications = realNotifications.filter(
        n => !savedState.deleted.includes(n.id)
      );

      // Marcar notificações lidas
      filteredNotifications.forEach(n => {
        if (savedState.read.includes(n.id)) {
          n.read = true;
        }
      });

      // Se não houver notificações reais, usar exemplos
      if (realNotifications.length === 0) {
        const mockNotifications = [
      {
        id: 1,
        type: 'warning',
        title: 'Estoque Baixo',
        message: 'Mouse Gamer está com apenas 2 unidades em estoque',
        icon: Package,
        color: 'amber',
        time: '5 min atrás',
        read: false
      },
      {
        id: 2,
        type: 'warning',
        title: 'Estoque Baixo',
        message: 'Teclado Mecânico atingiu o estoque mínimo (5 unidades)',
        icon: Package,
        color: 'amber',
        time: '15 min atrás',
        read: false
      },
      {
        id: 3,
        type: 'error',
        title: 'Estoque Crítico',
        message: 'Notebook Dell sem estoque disponível!',
        icon: AlertCircle,
        color: 'red',
        time: '1 hora atrás',
        read: false
      },
      {
        id: 4,
        type: 'info',
        title: 'Novo Produto',
        message: 'Produto "Mouse Logitech" foi cadastrado',
        icon: Package,
        color: 'blue',
        time: '2 horas atrás',
        read: true
      },
      {
        id: 5,
        type: 'success',
        title: 'Venda Realizada',
        message: 'Venda de R$ 4.550,00 registrada no sistema',
        icon: DollarSign,
        color: 'green',
        time: '3 horas atrás',
        read: true
      }
        ];
        
        // Aplicar estado salvo aos mocks também
        const savedState = getSavedNotifications();
        const filteredMocks = mockNotifications.filter(
          n => !savedState.deleted.includes(n.id)
        );
        filteredMocks.forEach(n => {
          if (savedState.read.includes(n.id)) {
            n.read = true;
          }
        });
        
        setNotifications(filteredMocks);
      } else {
        // Ordenar por tempo (mais recentes primeiro)
        filteredNotifications.sort((a, b) => {
          const timeA = parseTime(a.time);
          const timeB = parseTime(b.time);
          return timeA - timeB;
        });
        setNotifications(filteredNotifications);
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calcular tempo relativo
  const calcularTempo = (date) => {
    const diff = Date.now() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Agora';
    if (minutes < 60) return `${minutes} min atrás`;
    if (hours < 24) return `${hours} hora${hours > 1 ? 's' : ''} atrás`;
    return `${days} dia${days > 1 ? 's' : ''} atrás`;
  };

  // Parse tempo para ordenação
  const parseTime = (timeStr) => {
    if (timeStr === 'Agora') return 0;
    if (timeStr.includes('min')) return parseInt(timeStr);
    if (timeStr.includes('hora')) return parseInt(timeStr) * 60;
    if (timeStr.includes('dia')) return parseInt(timeStr) * 1440;
    return 999999;
  };

  const markAsRead = (id) => {
    // Atualizar estado local
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
    
    // Salvar no localStorage
    const savedState = getSavedNotifications();
    if (!savedState.read.includes(id)) {
      savedState.read.push(id);
      saveNotificationState(savedState);
    }
    
    // Disparar evento para atualizar badge
    window.dispatchEvent(new Event('notificationsUpdated'));
  };

  const markAllAsRead = () => {
    // Atualizar estado local
    const updatedNotifications = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updatedNotifications);
    
    // Salvar no localStorage
    const savedState = getSavedNotifications();
    notifications.forEach(n => {
      if (!savedState.read.includes(n.id)) {
        savedState.read.push(n.id);
      }
    });
    saveNotificationState(savedState);
    
    // Disparar evento para atualizar badge
    window.dispatchEvent(new Event('notificationsUpdated'));
  };

  const deleteNotification = (id) => {
    // Atualizar estado local
    setNotifications(notifications.filter(n => n.id !== id));
    
    // Salvar no localStorage
    const savedState = getSavedNotifications();
    if (!savedState.deleted.includes(id)) {
      savedState.deleted.push(id);
      saveNotificationState(savedState);
    }
    
    // Disparar evento para atualizar badge
    window.dispatchEvent(new Event('notificationsUpdated'));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!isOpen) return null;

  const colorClasses = {
    amber: {
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-200 dark:border-amber-800',
      text: 'text-amber-700 dark:text-amber-300',
      icon: 'text-amber-600 dark:text-amber-400'
    },
    red: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      text: 'text-red-700 dark:text-red-300',
      icon: 'text-red-600 dark:text-red-400'
    },
    blue: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      text: 'text-blue-700 dark:text-blue-300',
      icon: 'text-blue-600 dark:text-blue-400'
    },
    green: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      text: 'text-green-700 dark:text-green-300',
      icon: 'text-green-600 dark:text-green-400'
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 animate-fade-in"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full md:w-96 bg-white dark:bg-gray-900 shadow-2xl z-50 animate-slide-in-right overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6" />
              <h2 className="text-2xl font-bold">Notificações</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-blue-100 text-sm">
              {unreadCount} não lida{unreadCount !== 1 ? 's' : ''}
            </p>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>
        </div>

        {/* Lista de Notificações */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {notifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                Nenhuma notificação
              </p>
            </div>
          ) : (
            notifications.map((notification) => {
              const Icon = notification.icon;
              const colors = colorClasses[notification.color];

              return (
                <div
                  key={notification.id}
                  className={`p-4 border-2 rounded-xl transition-all ${colors.bg} ${colors.border} ${
                    notification.read ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${colors.bg}`}>
                      <Icon className={`w-5 h-5 ${colors.icon}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className={`font-semibold ${colors.text}`}>
                          {notification.title}
                        </h4>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1"></div>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                        {notification.message}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {notification.time}
                        </span>
                        
                        <div className="flex gap-2">
                          {!notification.read && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="text-xs px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-1"
                            >
                              <Check className="w-3 h-3" />
                              Marcar como lida
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="text-xs px-3 py-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            💡 Dica: Configure alertas na aba "Notificações" em Configurações
          </p>
        </div>
      </div>
    </>
  );
}

